import { Button, DropdownMenu, IconButton } from '@nexelem/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { TaskStatus } from '@nexelem/graphne-api';
import { useCustomTranslation } from '../../../../../../hooks';
import { getAbsolutePath, ROUTES } from '../../../../../../routes/config';
import { useProductionTaskLocate } from '../../../../../../components/organisms/ProductionPlans/Gantt/hooks';
import {
  faArrowRight,
  faCircleLocationArrow,
  faDiagramProject,
  faEllipsisVertical,
  faSquareList,
  faSquareXmark,
} from '@fortawesome/pro-light-svg-icons';
import { useGetSimulationStatus } from '../../../../../../components/organisms/ProductionPlans/GanttSimulation/hooks/useGetSimulationStatus';
import { openBillOfMaterialModal } from '../../../BillOfMaterialModal';
import { useAuthContext } from '../../../../../../providers/AuthProvider';
import { PERMS } from '../../../../../../routes/permissions';

export type ExternalId = {
  id: string | number;
};

type TaskDetailsDrawerFooterProps = {
  taskId: number;
  queueId?: number;
  taskStartDate?: string;
  taskStatus: TaskStatus;
  isTaskNotPlanned: boolean;
  isGraphDrawer: boolean;
  isKanban: boolean;
  onModalToggle: () => void;
  onRemoveModalToggle: () => void;
  externalId: ExternalId;
};

export const TaskDetailsDrawerFooter = ({
  queueId,
  taskId,
  taskStartDate,
  taskStatus,
  isTaskNotPlanned,
  isGraphDrawer,
  isKanban,
  onModalToggle,
  onRemoveModalToggle,
  externalId,
}: TaskDetailsDrawerFooterProps) => {
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const locate = useProductionTaskLocate();
  const location = useLocation();
  const { disableActions } = useGetSimulationStatus();
  const { hasPermission } = useAuthContext();
  const canEditPlan = hasPermission(PERMS.PERM_4);
  const canAddToPlan = hasPermission(PERMS.PERM_6);
  const canViewLotDetails = hasPermission(PERMS.PERM_7);
  const canViewGraph = hasPermission(PERMS.PERM_9);

  const handleLocateClick = () => {
    const startDate = taskStartDate ? new Date(taskStartDate) : undefined;

    if (taskId && startDate && queueId) {
      locate({ taskId, queueId, taskStartDate: startDate });
    }
  };

  const isValidExternalId = !!Number(externalId.id);

  return (
    <div className="box-border flex min-h-15 w-full items-center justify-end gap-2 border-t border-border-primary bg-surface-secondary px-6">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <IconButton Icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {!isTaskNotPlanned && !isKanban && (
            <DropdownMenu.Button
              StartIcon={<FontAwesomeIcon icon={faCircleLocationArrow} />}
              onClick={handleLocateClick}
            >
              {tWrapper('locate')}
            </DropdownMenu.Button>
          )}
          {canViewGraph && !isGraphDrawer && isValidExternalId && (
            <DropdownMenu.Button
              component={Link}
              StartIcon={<FontAwesomeIcon icon={faDiagramProject} />}
              state={{ returnUrl: getReturnUrl(location) }}
              to={generatePath(getAbsolutePath(ROUTES.plan.graph), {
                id: taskId.toString(),
              })}
            >
              {tWrapper('showGraph')}
            </DropdownMenu.Button>
          )}
          <DropdownMenu.Button
            StartIcon={<FontAwesomeIcon icon={faSquareList} />}
            onClick={() => openBillOfMaterialModal({ taskId: taskId.toString() })}
          >
            {t('products.productionPlan.billOfMaterial.showBillOfMaterial')}
          </DropdownMenu.Button>
          {canEditPlan && !isTaskNotPlanned && taskStatus === TaskStatus.Pending && (
            <DropdownMenu.Button
              disabled={disableActions}
              StartIcon={<FontAwesomeIcon icon={faSquareXmark} />}
              onClick={onRemoveModalToggle}
            >
              {tWrapper('removeFromPlan')}
            </DropdownMenu.Button>
          )}
          {canViewLotDetails && isTaskNotPlanned && isValidExternalId && (
            <DropdownMenu.Button
              component={Link}
              StartIcon={<FontAwesomeIcon icon={faArrowRight} />}
              state={{ returnUrl: getReturnUrl(location) }}
              to={generatePath(getAbsolutePath(ROUTES.productionLots.details.full), {
                id: externalId.id as number,
                tab: 'reports',
              })}
            >
              {tWrapper('showDetails')}
            </DropdownMenu.Button>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {canViewGraph && !isTaskNotPlanned && !isValidExternalId && !isGraphDrawer && (
        <Button
          component={Link}
          StartIcon={<FontAwesomeIcon icon={faDiagramProject} />}
          state={{ returnUrl: getReturnUrl(location) }}
          variant="text"
          to={generatePath(getAbsolutePath(ROUTES.plan.graph), {
            id: taskId.toString(),
          })}
        >
          {tWrapper('showGraph')}
        </Button>
      )}

      {canViewLotDetails && !isTaskNotPlanned && isValidExternalId && (
        <Button
          component={Link}
          StartIcon={<FontAwesomeIcon icon={faArrowRight} />}
          state={{ returnUrl: getReturnUrl(location) }}
          variant="text"
          to={generatePath(getAbsolutePath(ROUTES.productionLots.details.full), {
            id: externalId.id as number,
            tab: 'reports',
          })}
        >
          {tWrapper('showDetails')}
        </Button>
      )}

      {canAddToPlan && isTaskNotPlanned && (
        <Button disabled={disableActions} variant="contained" onClick={onModalToggle}>
          {tWrapper('addToPlan')}
        </Button>
      )}
    </div>
  );
};

const getReturnUrl = (location: ReturnType<typeof useLocation>) => `${location.pathname}${location.search}`;
